import { Card } from '@workspace/ui/components/custom/card';
import { cn } from '@workspace/ui/lib';
import { Folder } from 'lucide-react';
import { useEffect, useState } from 'react';

export interface Folder {
  id: number;
  name: string;
  value: string;
  parent_id: number | null;
  count: number;
}
export interface FolderBreadcrumbsProps {
  className?: string;
  rootFolderName?: string;
  currentFolder: Folder | null;
  setCurrentFolder: (folder: Folder | null) => void;
}

export function FolderBreadcrumbs({
  className,
  rootFolderName = 'Folders',
  currentFolder,
  setCurrentFolder,
}: FolderBreadcrumbsProps) {
  const [breadcrumbPath, setBreadcrumbPath] = useState<Folder[]>([]);

  useEffect(() => {
    if (!currentFolder) {
      setBreadcrumbPath([]);
      setCurrentFolder(null);
      return;
    }

    setBreadcrumbPath((prevPath) => {
      const existingIndex = prevPath.findIndex((f) => f.id === currentFolder.id);
      if (existingIndex !== -1) {
        return prevPath.slice(0, existingIndex + 1);
      }
      return [...prevPath, currentFolder];
    });
  }, [setCurrentFolder, currentFolder]);

  return (
    breadcrumbPath.length > 0 && (
      <div
        className={cn(
          'scrollbar-hide flex w-full items-center gap-2 overflow-y-scroll text-base text-neutral-200',
          className
        )}
      >
        <p className="cursor-pointer hover:text-gray-300" onClick={() => setCurrentFolder(null)}>
          {rootFolderName}
        </p>

        {breadcrumbPath.map((pathFolder, index) => (
          <div key={pathFolder.id} className="flex items-center gap-2">
            <span className="text-gray-400">/</span>
            <p
              className={`no-wrap cursor-pointer whitespace-nowrap hover:text-gray-300 ${
                index === breadcrumbPath.length - 1 ? 'font-medium text-gray-200' : ''
              }`}
              onClick={() => setCurrentFolder(pathFolder)}
            >
              {pathFolder.name}
            </p>
          </div>
        ))}
      </div>
    )
  );
}

export interface FolderCardProps {
  className?: string;
  name: string;
  count: number;
  onClick: () => void;
  disabled?: boolean;
}

export function FolderCard({ className, name, count, onClick, disabled = false }: FolderCardProps) {
  return (
    <Card
      className={cn(
        className,
        'flex flex-col justify-between p-6',
        disabled
          ? 'bg-grey-100 dark:bg-grey-600'
          : 'bg-grey-0 dark:bg-grey-800 cursor-pointer hover:shadow-md'
      )}
      onClick={disabled ? undefined : onClick}
    >
      <div className="mb-4 flex items-start justify-between">
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-xl',
            disabled ? 'bg-grey-300 dark:bg-grey-700' : 'dark:bg-grey-700 bg-neutral-200'
          )}
        >
          <Folder
            className={cn(
              'h-5 w-5 stroke-2!',
              disabled ? 'text-grey-100 dark:text-grey-500' : 'text-white'
            )}
          />
        </div>
        {/* <MoreVertical
          className={cn(
            'h-5 w-5 stroke-2!',
            disabled
              ? 'text-grey-500 cursor-not-allowed'
              : 'cursor-pointer text-neutral-200 dark:text-neutral-100'
          )}
        /> */}
      </div>

      <div className="space-y-2">
        <h3
          className={cn(
            'text-base leading-tight',
            disabled ? 'text-grey-400 dark:text-grey-400' : 'text-grey-600 dark:text-neutral-100'
          )}
        >
          {name}
        </h3>
        <p
          className={cn(
            'text-sm',
            disabled ? 'text-grey-400 dark:text-grey-400' : 'text-grey-600 dark:text-neutral-100'
          )}
        >
          {count} {count === 1 ? 'item' : 'items'}
        </p>
      </div>
    </Card>
  );
}
